import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

export const UNFULFILLED_REASONS = [
  { value: "ESTOQUE_INSUFICIENTE", label: "Estoque insuficiente" },
  { value: "FALTA_MATERIA_PRIMA", label: "Falta de matéria-prima" },
  { value: "SEM_DISPONIBILIDADE_PRODUCAO", label: "Produto sem disponibilidade para produção" },
  { value: "DESCONTINUADO", label: "Produto descontinuado" },
  { value: "PROBLEMA_TECNICO", label: "Problema técnico" },
  { value: "OUTRO", label: "Outro" },
];

export const approvedOf = (it) =>
  it.approved_quantity == null ? Number(it.quantity) : Number(it.approved_quantity);

/** Frase do atendimento. Usa só o que a API já manda. */
export const fulfillmentText = (r) => {
  const f = r.fulfillment;
  if (!f || !f.requested_units) return null;
  const base = `${f.approved_units} de ${f.requested_units} unidades`;
  if (f.status === "NAO_ATENDIDO") return `Pedido não atendido — ${base}`;
  if (f.status === "PARCIAL") return `Pedido parcialmente atendido — ${base}`;
  return `Pedido aceito — ${base}`;
};

const itemKey = (it, idx) => `${it.product_id}-${it.variation_id || idx}`;

/** Análise de atendimento: define quanto será atendido por item. */
export function FulfillmentModal({ request: r, onClose, onSaved }) {
  const [rows, setRows] = useState(() =>
    (r.items || []).map((it) => ({
      product_id: it.product_id,
      variation_id: it.variation_id || null,
      name: it.name,
      requested: Number(it.quantity),
      locked: !!it.from_kit_id,
      approved: String(approvedOf(it)),
      reason: it.unfulfilled_reason || "",
      note: it.unfulfilled_note || "",
    }))
  );
  const [busy, setBusy] = useState(false);

  const set = (idx, patch) =>
    setRows((list) => list.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const requested = rows.reduce((s, row) => s + row.requested, 0);
  const approved = rows.reduce((s, row) => s + Math.min(Number(row.approved || 0), row.requested), 0);

  const submit = async () => {
    for (const row of rows) {
      const qty = Number(row.approved);
      if (Number.isNaN(qty) || qty < 0 || qty > row.requested) {
        toast.error(`Quantidade inválida em ${row.name} (máx. ${row.requested}).`);
        return;
      }
      if (qty < row.requested && !row.reason) {
        toast.error(`Informe o motivo da quantidade não atendida de ${row.name}.`);
        return;
      }
    }
    setBusy(true);
    try {
      await api.put(`/replenishments/${r.id}/fulfillment`, {
        items: rows.map((row) => ({
          product_id: row.product_id,
          variation_id: row.variation_id,
          approved_quantity: Number(row.approved),
          reason: row.reason || null,
          note: row.note || "",
        })),
      });
      toast.success("Atendimento definido. O parceiro foi avisado.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={onClose}
      title="Analisar atendimento"
      description={`${r.number || ""} · ${r.partner_name || ""}`}
      testid="fulfillment-modal"
      wide
    >
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Defina quanto será realmente atendido de cada item. A quantidade solicitada pelo
          parceiro nunca é alterada e todo ajuste fica registrado no histórico.
        </p>
        <div className="space-y-3">
          {rows.map((row, idx) => {
            const qty = Number(row.approved || 0);
            const missing = Math.max(row.requested - (Number.isNaN(qty) ? 0 : qty), 0);
            return (
              <div
                key={`${row.product_id}-${row.variation_id || idx}`}
                className="rounded-xl border border-white/10 bg-secondary/30 p-3"
                data-testid={`fulfillment-row-${row.product_id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{row.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      Solicitado: <span className="font-num">{row.requested}</span>
                      {missing > 0 && (
                        <>
                          {" · "}não atendido: <span className="font-num">{missing}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase text-muted-foreground">Aceito</span>
                    <Input
                      type="number"
                      min={0}
                      max={row.requested}
                      disabled={row.locked}
                      value={row.approved}
                      onChange={(e) => set(idx, { approved: e.target.value })}
                      data-testid={`fulfillment-qty-${row.product_id}`}
                      className="h-10 w-20 text-center font-num"
                    />
                  </div>
                </div>
                {row.locked && (
                  <p className="mt-2 text-[11px] text-amber-300">
                    Item de kit liberado — ajuste indisponível nesta análise.
                  </p>
                )}
                {!row.locked && missing > 0 && (
                  <div className="mt-3 space-y-2">
                    <NativeSelect
                      value={row.reason}
                      onChange={(v) => set(idx, { reason: v })}
                      options={UNFULFILLED_REASONS}
                      placeholder="Motivo da quantidade não atendida"
                      data-testid={`fulfillment-reason-${row.product_id}`}
                    />
                    <Textarea
                      rows={2}
                      value={row.note}
                      onChange={(e) => set(idx, { note: e.target.value })}
                      placeholder="Observação (opcional)"
                      data-testid={`fulfillment-note-${row.product_id}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p
          className="rounded-xl border border-white/10 bg-secondary/40 px-4 py-3 text-sm"
          data-testid="fulfillment-summary"
        >
          {approved >= requested
            ? `Pedido aceito — ${approved} de ${requested} unidades`
            : approved <= 0
            ? `Pedido não atendido — ${approved} de ${requested} unidades`
            : `Pedido parcialmente atendido — ${approved} de ${requested} unidades`}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full"
                  data-testid="fulfillment-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={submit} disabled={busy} className="flex-1 rounded-full"
                  data-testid="fulfillment-save-btn">
            {busy ? "Salvando..." : "Confirmar atendimento"}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}

/** Histórico do item: solicitado -> aprovado -> reduções. */
export function ItemFulfillmentLine({ item, testid }) {
  const approved = approvedOf(item);
  const missing = Number(item.quantity) - approved;
  if (item.approved_quantity == null) return null;
  return (
    <p className="mt-0.5 text-[11px] text-muted-foreground" data-testid={testid}>
      solicitado <span className="font-num">{item.quantity}</span> · aceito{" "}
      <span className="font-num text-foreground">{approved}</span>
      {missing > 0 && (
        <>
          {" · "}
          <span className="text-amber-300">
            não atendido <span className="font-num">{missing}</span>
            {item.unfulfilled_reason_label ? ` — ${item.unfulfilled_reason_label}` : ""}
          </span>
        </>
      )}
      {item.unfulfilled_note ? ` · ${item.unfulfilled_note}` : ""}
    </p>
  );
}

export { itemKey };
